/**
 * Render planning: turns a theme's scene recipe into an ordered list of
 * passes the renderer can draw quickly every frame.
 *
 * - A **static** pass is a run of consecutive layers at the same (quantised)
 *   parallax depth whose pixels never change. The renderer paints it once
 *   per (theme, viewport size) into an offscreen bitmap and just blits it.
 * - A **live** pass is the moving part of one animated layer (drifting
 *   clouds, waves, swaying palms…), repainted every frame.
 *
 * Painting order is preserved exactly: a layer's still part always lands in
 * the static pass right before its own live pass.
 */
import { deriveSeed, type SceneLayer, type ThemePack } from '@dropstack/core';
import { quantizeDepth } from './layout.ts';
import { hashString } from './noise.ts';
import { isAnimated, painterFor } from './painters/index.ts';

export interface PlannedLayer {
  layer: SceneLayer;
  /** Deterministic per-layer seed (theme id + the layer's `seed` or index). */
  seed: number;
  /** Quantised parallax depth, 0 … 1 in eighths. */
  depth: number;
  /** Repainted every frame. When false, any moving part is baked at time 0. */
  animated: boolean;
}

export type ScenePass = { kind: 'static'; depth: number; layers: PlannedLayer[] } | { kind: 'live'; depth: number; layer: PlannedLayer };

/** Seed for a layer: stable across sessions, so a theme always paints the same landscape. */
export function layerSeed(themeId: string, layer: SceneLayer, index: number): number {
  return deriveSeed(hashString(themeId), layer.seed ?? index);
}

/** Seed for a theme's particle field. */
export function particleSeed(themeId: string): number {
  return hashString(`${themeId}:particles`);
}

/**
 * Plan every layer of a theme. With `animate` off (reduced motion, previews)
 * nothing is animated and there is no parallax, so the whole scene collapses
 * into one static pass.
 */
export function planLayers(theme: ThemePack, animate: boolean): PlannedLayer[] {
  return theme.scene.map((layer, index) => ({
    layer,
    seed: layerSeed(theme.id, layer, index),
    depth: animate ? quantizeDepth(layer.depth) : 0,
    animated: animate && isAnimated(layer),
  }));
}

/** Group planned layers into static and live passes, preserving paint order. */
export function planPasses(layers: readonly PlannedLayer[]): ScenePass[] {
  const passes: ScenePass[] = [];
  let open: Extract<ScenePass, { kind: 'static' }> | null = null;
  for (const pl of layers) {
    const painter = painterFor(pl.layer);
    const hasStatic = painter.still !== undefined || (!pl.animated && painter.moving !== undefined);
    if (hasStatic) {
      if (!open || open.depth !== pl.depth) {
        open = { kind: 'static', depth: pl.depth, layers: [] };
        passes.push(open);
      }
      open.layers.push(pl);
    }
    if (pl.animated) {
      passes.push({ kind: 'live', depth: pl.depth, layer: pl });
      open = null; // later static layers must be painted on top of this live one
    }
  }
  return passes;
}
